/*
  <shintop-recommendations> — lazy self-renderer for the "You may also like" band.

  The element ships empty (recommendations.performed is false on the product page). Once
  it nears the viewport it fetches its own section through Shopify's product
  recommendations route and swaps in the server-rendered band. All markup, prices and
  money formatting are Liquid's; this file only moves HTML. On any failure it stays
  empty — an absent band, never a broken one.

  ON A PRODUCT PAGE IT ALSO CHOOSES (UIQ-017, 2026-10-10). The page puts four facts on the
  element (see the section): what the page already shows, the product's categories, their
  departments, and how many cards to keep. Each card Shopify returns is then ranked:

    1  shares a category with the product      (biscuits under a biscuit)
    2  shares a department                     (snacks under a snack)
    3  anything else                           — only when nothing closer is left

  keeping Shopify's own order inside a tier, buyable before sold out, and dropping every
  card the page has already shown. When tiers 1 and 2 cannot fill the row, the category
  collections — then the department collections — are fetched through the same section
  (its collection branch) and their cards join the pool. Ten is the route's ceiling, and
  after the family is removed most products keep six to ten picks, almost half of them from
  another department: the top-up is what keeps the row both full and relevant.

  IT STARTS WHEN THE PAGE HAS GONE IDLE, NOT WHEN THE BAND IS NEAR (S05-002, audit 2026-10-14
  chunk 12). An empty element and a 600px observer margin meant the band was fetched as the
  shopper approached it and inserted while it was on screen: on a fast scroll the footer jumped
  (lab CLS 0.425 on the yakisoba, 0.24 on the okonomiyaki kit — whose band is ONE request, so no
  shorter chain could have fixed it). Reserving its height is not possible for a band that may
  render nothing. So it starts after `load`, on idle, and is in place long before anyone scrolls
  that far; the observer stays as the earlier trigger for a shopper who gets there first. The
  price is that a shopper who never scrolls down still fetches the band (the images in it stay
  lazy). Same shape, same reason, in shintop-recently-viewed.js — keep the two in step.

  The report's other remedy — fire the category and department top-ups together — was measured
  and declined: on nine product pages, ranking the same fetched responses both ways, it asked for a
  department collection the row never used on seven (each ~7 KB on the wire, ~120 KB of HTML to
  parse), now on every product view, to save one round trip on the few products whose category
  falls short. With the start moved to idle the band is in place before that round trip matters.

  Nothing here makes a card. Every <li> was rendered by Liquid; this file picks and orders.
*/
if (!customElements.get('shintop-recommendations')) {
  const fenced = (value) => (value || '').split(',').filter(Boolean);

  /* Runs `run` once: after `load`, when the browser is idle — or sooner, if the band nears the
     viewport first. The twin of this is in shintop-recently-viewed.js. */
  const startSoon = (el, run) => {
    let started = false;
    const go = () => {
      if (started || !el.isConnected) return;
      started = true;
      el.observer?.disconnect();
      run();
    };
    el.observer = new IntersectionObserver((entries) => entries[0].isIntersecting && go(), {
      rootMargin: '0px 0px 600px 0px',
    });
    el.observer.observe(el);
    const idle = () => (window.requestIdleCallback ? requestIdleCallback(go, { timeout: 2000 }) : setTimeout(go, 200));
    if (document.readyState === 'complete') idle();
    else window.addEventListener('load', idle, { once: true });
  };

  customElements.define(
    'shintop-recommendations',
    class ShintopRecommendations extends HTMLElement {
      connectedCallback() {
        if (!this.dataset.url) return;
        startSoon(this, () => this.load());
      }

      disconnectedCallback() {
        this.observer?.disconnect();
      }

      async fetchDoc(url) {
        const response = await fetch(url);
        if (!response.ok) return null;
        return new DOMParser().parseFromString(await response.text(), 'text/html');
      }

      async load() {
        try {
          const doc = await this.fetchDoc(this.dataset.url);
          const fresh = doc?.querySelector('shintop-recommendations');
          if (!fresh || !fresh.innerHTML.trim().length) return;
          if (this.dataset.max) await this.rank(fresh);
          /* One card is not a row (S09-06, 2026-09-18). The section now sends the
             row's shell even when no card of its own survived the cart (S03-024), so this test, not
             the shell's presence, is what keeps an empty band off the page — on /cart's unranked
             path as much as here. */
          if (fresh.querySelectorAll('.shintop-carousel__track > li').length >= 2) this.innerHTML = fresh.innerHTML;
        } catch {
          /* leave the band absent */
        }
      }

      /* Collects the cards of one response into the pool, first sighting wins. */
      gather(root, pool) {
        const map = {};
        root.querySelectorAll('script[data-shintop-rec-map]').forEach((node) => {
          try {
            Object.assign(map, JSON.parse(node.textContent));
          } catch {
            /* a card with no entry ranks last, never vanishes */
          }
        });
        root.querySelectorAll('.shintop-carousel__track > li[data-shintop-handle]').forEach((li) => {
          const handle = li.dataset.shintopHandle;
          if (this.exclude.has(handle) || pool.has(handle)) return;
          const facts = map[handle] || { c: [] };
          let tier = 3;
          if (facts.c.some((c) => this.categories.has(c))) tier = 1;
          else if (facts.c.some((c) => this.departments.has(c))) tier = 2;
          /* Availability is the card's own attribute (R09-1), the one recently viewed sorts by too. */
          pool.set(handle, { li, tier, sold: li.dataset.shintopAvailable === 'false', order: pool.size });
        });
      }

      count(pool, maxTier) {
        let n = 0;
        pool.forEach((card) => {
          if (card.tier <= maxTier && !card.sold) n += 1;
        });
        return n;
      }

      async topUp(handles, pool) {
        const base = this.dataset.collectionsUrl || '/collections';
        const docs = await Promise.all(
          handles.map((h) =>
            this.fetchDoc(`${base}/${encodeURIComponent(h)}?section_id=shintop-recommendations`).catch(() => null),
          ),
        );
        docs.forEach((doc) => doc && this.gather(doc, pool));
      }

      async rank(fresh) {
        const max = parseInt(this.dataset.max, 10) || 8;
        this.exclude = new Set(fenced(this.dataset.exclude));
        this.categories = new Set(fenced(this.dataset.categories));
        this.departments = new Set(fenced(this.dataset.departments));

        const pool = new Map();
        this.gather(fresh, pool);
        if (this.count(pool, 1) < max && this.categories.size) await this.topUp([...this.categories], pool);
        if (this.count(pool, 2) < max && this.departments.size) await this.topUp([...this.departments], pool);

        const chosen = [...pool.values()]
          .sort((a, b) => a.sold - b.sold || a.tier - b.tier || a.order - b.order)
          .slice(0, max);

        const track = fresh.querySelector('.shintop-carousel__track');
        if (!track) return;
        /* One card is not a row — the same floor, applied after the exclusion. */
        if (chosen.length < 2) {
          fresh.innerHTML = '';
          return;
        }
        track.replaceChildren(...chosen.map((card) => card.li));
      }
    },
  );
}
